/**
 * Cylinder carousel — slides positioned on a 3D Y-axis cylinder.
 * No dependencies. Pause by holding mouse/touch.
 */

const CARD_W = 175;
const CARD_H = 263;          // px — 155 × 3/2 portrait ratio
const SPEED         = 6 / 1000;      // degrees per millisecond = full rotation in 30s


export function initCylinderCarousel(selector = '#lvCylinder') {
    const wrap = document.querySelector(selector);
    if (!wrap) return null;

    const ring   = wrap.querySelector('.lv-cylinder-ring');
    const slides = [...wrap.querySelectorAll('.lv-cylinder-slide')];
    const dots   = [...wrap.querySelectorAll('.lv-cylinder-dot')];
    const n      = slides.length;
    if (!n || !ring) return null;

    // --- geometry ---
    const theta  = 360 / n;
    const radius = Math.round(CARD_W / (2 * Math.tan(Math.PI / n)));

    slides.forEach((slide, i) => {
        slide.style.transform = `rotateY(${i * theta}deg) translateZ(${radius}px)`;
    });

    // --- state ---
    let deg         = 0;
    let paused      = false;
    let lastTs      = null;
    let rafId       = null;
    let activeIdx   = 0;

    // --- helpers ---
    function activeFromDeg(d) {
        const norm = ((-d % 360) + 360) % 360;
        return Math.round(norm / theta) % n;
    }

    function updateOpacityBlur(d) {
        const norm = ((-d % 360) + 360) % 360;
        slides.forEach((slide, i) => {
            const slideDeg = i * theta;
            // angular diff from front: 0 = facing viewer, 180 = directly behind
            const diff = Math.abs(((norm - slideDeg + 540) % 360) - 180);
            // cosine falloff — smooth and natural
            const opacity = Math.max(0.08, Math.cos((diff / 180) * (Math.PI / 2)));
            const blur    = diff < 55 ? 0 : Math.min(3, ((diff - 55) / 125) * 3);
            slide.style.opacity = opacity.toFixed(3);
            slide.style.filter  = blur > 0.1 ? `blur(${blur.toFixed(1)}px)` : 'none';
        });
    }

    function syncDots(idx) {
        dots.forEach((d, i) => d.classList.toggle('is-active', i === idx));
    }

    function syncVideos(idx) {
        slides.forEach((slide, i) => {
            const v = slide.querySelector('video');
            if (!v) return;
            if (i === idx) {
                v.currentTime = 0;
                v.play().catch(() => {});
            } else if (!v.paused) {
                v.pause();
            }
        });
    }

    // --- animation loop ---
    function tick(ts) {
        if (!lastTs) lastTs = ts;
        const elapsed = Math.min(ts - lastTs, 64); // cap at ~2 frames to avoid jumps
        lastTs = ts;

        if (!paused) {
            deg -= SPEED * elapsed;
            ring.style.transform = `rotateY(${deg}deg)`;
            updateOpacityBlur(deg);

            const next = activeFromDeg(deg);
            if (next !== activeIdx) {
                activeIdx = next;
                syncDots(activeIdx);
                syncVideos(activeIdx);
            }
        }

        rafId = requestAnimationFrame(tick);
    }

    // --- pause on hold ---
    const doPause  = () => { paused = true; };
    const doResume = () => { paused = false; lastTs = null; };

    wrap.addEventListener('mousedown',   doPause);
    wrap.addEventListener('touchstart',  doPause,  { passive: true });
    wrap.addEventListener('mouseup',     doResume);
    wrap.addEventListener('mouseleave',  doResume);
    wrap.addEventListener('touchend',    doResume);
    wrap.addEventListener('touchcancel', doResume);

    // --- boot ---
    updateOpacityBlur(0);
    syncVideos(0);
    rafId = requestAnimationFrame(tick);

    return { destroy: () => cancelAnimationFrame(rafId) };
}